/**
 * submit-error.ts — F-009.4 follow-up (UX errori al submit pratica).
 *
 * Traduce gli errori applicativi del `POST /pratiche/:id/submit` in messaggi
 * comprensibili all'utente + un'eventuale call-to-action (es. "Ricarica il
 * wallet"). Centralizzare la mappatura qui evita di duplicare le stringhe tra
 * `DenseSubmitFooter` (AGENTE/COMMERCIALISTA) e `CreateSubmitStep` (privati).
 *
 * I codici provengono dal backend (`apps/api/src/routes/pratica/submit.ts`):
 *  - 402 `INSUFFICIENT_FUNDS`   → saldo wallet insufficiente (oltre fido).
 *  - 403 `WALLET_BLOCKED`       → wallet bloccato dall'admin.
 *  - 409 `LISTINO_NOT_CONFIGURED` → tariffa mancante per la tipologia.
 *
 * Detection via duck-typing sul campo `code` (string) invece di `instanceof
 * PraticaApiError`: i test unitari mockano il modulo `pratica-client`
 * sostituendo la classe, quindi un check `instanceof` sarebbe fragile.
 */

/** Percorso della pagina wallet (vedi App.tsx). */
const WALLET_PATH = '/app/wallet';

export interface SubmitErrorView {
  /** Messaggio leggibile da mostrare nell'alert. */
  message: string;
  /** CTA opzionale: presente solo quando l'utente può auto-rimediare. */
  action?: { label: string; href: string };
}

/** Estrae il codice applicativo da un errore, se presente (duck-typing). */
function extractCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

const FALLBACK_MESSAGE = "Errore durante l'invio della pratica. Riprova.";

/**
 * Mappa un errore di submit in un messaggio utente + eventuale CTA.
 * Per codici non riconosciuti usa il `message` dell'errore (già localizzato
 * lato API) o un fallback generico.
 */
export function resolveSubmitError(err: unknown): SubmitErrorView {
  switch (extractCode(err)) {
    case 'INSUFFICIENT_FUNDS':
      return {
        message:
          'Saldo insufficiente per inviare la pratica. Ricarica il wallet e riprova.',
        action: { label: 'Ricarica il wallet', href: WALLET_PATH },
      };
    case 'WALLET_BLOCKED':
      return {
        message:
          'Il tuo wallet è bloccato. Contatta il supporto per sbloccarlo prima di inviare la pratica.',
      };
    case 'LISTINO_NOT_CONFIGURED':
      return {
        message:
          'Il prezzo per questo tipo di pratica non è ancora configurato. Contatta il supporto.',
      };
    default: {
      const message =
        err instanceof Error && err.message ? err.message : FALLBACK_MESSAGE;
      return { message };
    }
  }
}
